/**
 * Konverterer VAPID public key (base64 URL-safe) til Uint8Array til PushManager.subscribe.
 */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Uden aktiv registrering må vi ikke vente på `serviceWorker.ready` — den resolver aldrig,
 * hvis ingen SW er registreret (fx `npm start`, hvor vi ikke auto-registrerer SW).
 */
export async function getCurrentPushSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export function isInstalledApp() {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches;
  const fullscreen = window.matchMedia?.('(display-mode: fullscreen)').matches;
  return !!(standalone || fullscreen || window.navigator.standalone === true);
}

/**
 * Beder om tilladelse hvis den mangler, og opretter push-abonnementet for enheden.
 * Returnerer subscription-JSON, eller null hvis det ikke lykkedes.
 */
export async function enablePushNotifications(vapidKey) {
  if (!isPushSupported() || !vapidKey) return null;
  if (Notification.permission === 'denied') return null;

  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return null;
  }

  const reg = await ensurePushServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
  }
  return sub.toJSON();
}

/**
 * Registrerer `public/sw.js` hvis nødvendigt og venter på aktiv worker — kræves før push subscribe.
 */
export async function ensurePushServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker understøttes ikke i denne browser.');
  }
  let registration = await navigator.serviceWorker.getRegistration();
  const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
  if (!registration) {
    registration = await navigator.serviceWorker.register(swUrl);
  }
  await navigator.serviceWorker.ready;
  return registration;
}
